/**
 * Enemy fire (GDD §5.2; concept L983–L1012): red tracers from helicopters and bridge guns, flak shells from
 * gunboats. Shots aim at where the jet will be, with spread; tracers stay lethal 0.6 s past their aim point, flak
 * bursts at it.
 */
import { config } from '../core/config.ts';
import type { GameSim } from '../sim/context.ts';
import { listPush, listRemoveAt, type CrashCause, type ShotKind } from '../sim/state.ts';
import { crash } from '../rules/crash.ts';

/** Seconds a tracer flies on past its aim point (concept L993). */
const TRACER_OVER = 0.6;
/** The concept's aim leads 90% of the jet's travel (L988). */
const LEAD = 0.9;
/** Concept rules also test height: tracers within 5 m, flak within 6 m (L1004, L1007; dropped by D8). */
const TRACER_DY = 5;
const FLAK_DY = 6;

/**
 * concept enemyFire (L983–L995). Takes the first free shot slot; with all 48 in flight the gun stays silent.
 * The aim spread and the autopilot's dodge roll come from the combat stream.
 */
export function enemyFire(sim: GameSim, x: number, y: number, z: number, kind: ShotKind, cause: CrashCause): void {
  let slot = -1;
  for (let k = 0; k < sim.shotUsed.length; k++)
    if (!sim.shotUsed[k]) {
      slot = k;
      break;
    }
  if (slot < 0) return;
  const rand = sim.streams.combat;
  const f = config.firing;
  const shooter = kind === 'flak' ? f.gunboat : cause === 'bridgeGuns' ? f.bridgeGun : f.heli;
  const spd = shooter.speed;
  const jet = sim.jet;
  // to = the jet now; tt = time of flight to it (three.js distanceTo: dx² + dy² + dz² in that order).
  let tx = jet.x;
  const ty = jet.y;
  let tz = jet.z;
  let dx = x - tx;
  let dy = y - ty;
  let dz = z - tz;
  let tt = Math.sqrt(dx * dx + dy * dy + dz * dz) / spd;
  tz -= jet.speed * tt * LEAD;
  tx += (rand() - 0.5) * (shooter.spread * 2);
  dx = x - tx;
  dy = y - ty;
  dz = z - tz;
  tt = Math.sqrt(dx * dx + dy * dy + dz * dz) / spd;
  // v = normalize(to − from) × speed (three.js: divide by the length, then scale).
  let vx = tx - x;
  let vy = ty - y;
  let vz = tz - z;
  const inv = 1 / (Math.sqrt(vx * vx + vy * vy + vz * vz) || 1);
  vx *= inv;
  vy *= inv;
  vz *= inv;
  vx *= spd;
  vy *= spd;
  vz *= spd;
  const s = sim.shotSlots[slot];
  if (!s) return;
  sim.shotUsed[slot] = 1;
  s.kind = kind;
  s.cause = cause;
  s.x = s.prevX = x;
  s.y = s.prevY = y;
  s.z = s.prevZ = z;
  s.vx = vx;
  s.vy = vy;
  s.vz = vz;
  s.life = tt;
  s.dodge = rand() < sim.run.skill;
  s.over = kind === 'flak' ? 0 : TRACER_OVER;
  listPush(sim.shots, s);
  sim.fx.muzzle(x, y, z, vx * 0.05, vz * 0.05, cause);
}

/** concept updShots (L1000–L1012): shots fly; tracers kill on contact, flak bursts at its aim point. */
export function stepShots(sim: GameSim, dt: number): void {
  const run = sim.run;
  const jet = sim.jet;
  const zone = config.crash.tracerZone;
  const flakR = config.crash.flakRadius;
  const heightChecks = sim.rules === 'concept';
  const list = sim.shots;
  for (let i = list.count - 1; i >= 0; i--) {
    const s = list.items[i];
    if (!s) continue;
    s.prevX = s.x;
    s.prevY = s.y;
    s.prevZ = s.z;
    s.x += s.vx * dt;
    s.y += s.vy * dt;
    s.z += s.vz * dt;
    s.life -= dt;
    const near = run.alive && run.invuln <= 0 && run.title <= 3;
    if (
      s.kind === 'tracer' &&
      near &&
      Math.abs(s.x - jet.x) < zone.across &&
      Math.abs(s.z - jet.z) < zone.along &&
      (!heightChecks || Math.abs(s.y - jet.y) < TRACER_DY)
    ) {
      s.life = -9;
      crash(sim, s.cause);
    }
    if (s.kind === 'flak' && s.life <= 0 && s.life > -1) {
      sim.fx.flak(s.x, s.y, s.z);
      if (near && Math.hypot(s.x - jet.x, s.z - jet.z) < flakR && (!heightChecks || Math.abs(s.y - jet.y) < FLAK_DY))
        crash(sim, 'flak');
      s.life = -9;
    }
    if (s.life < -s.over) {
      sim.shotUsed[s.slot] = 0;
      listRemoveAt(list, i);
    }
  }
}

/** Every shot disappears (a respawn or a new run, concept L1131). */
export function clearShots(sim: GameSim): void {
  sim.shots.count = 0;
  sim.shotUsed.fill(0);
}
